import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Search,
  TrendingDown,
  TrendingUp,
  UploadCloud,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useMoney } from '@/hooks/useMoney';
import { humanize } from '@/lib/format';
import { fieldLabel, fieldOf, plainErrorMessage, problemsOf } from '@/lib/errors';
import { Illustration, type IllustrationName } from './Illustration';

/**
 * What a status means, independent of what colour currently expresses it.
 *
 * The tone is the contract — it is what the badges expose as `data-tone` and
 * what the tests assert on — so the palette underneath can be restyled without
 * rewriting a single assertion.
 */
export type Tone = 'positive' | 'negative' | 'attention' | 'progress' | 'neutral';

/**
 * Tone to classes, written out in full.
 *
 * Tailwind generates CSS by scanning source text for literal class names, so
 * a composed `badge-${tone}` would name a rule that was never built.
 */
const BADGE: Record<Tone, string> = {
  positive: 'badge badge-positive',
  negative: 'badge badge-negative',
  attention: 'badge badge-attention',
  progress: 'badge badge-progress',
  neutral: 'badge badge-neutral',
};

/** Coloured status pill. Colour is derived from meaning, not from a lookup. */
export function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status)
    return (
      <span data-tone="neutral" className="muted">
        —
      </span>
    );
  const tone = toneFor(status);
  return (
    <span data-tone={tone} className={BADGE[tone]}>
      {humanize(status)}
    </span>
  );
}

/**
 * A pill whose tone the caller decides, for vocabularies `StatusBadge` does
 * not know — such as the vendor-facing invoice statuses.
 */
export function ToneBadge({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span data-tone={tone} className={BADGE[tone]}>
      {children}
    </span>
  );
}

function toneFor(status: string): Tone {
  const value = status.toUpperCase();
  if (
    [
      'PAID',
      'RECONCILED',
      'SETTLED_BY_ADVANCE',
      'APPROVED',
      'MATCHED',
      'ACTIVE',
      'COMPLETED',
      'SENT',
      'CONNECTED',
      'READY_FOR_REVIEW',
    ].includes(value)
  ) {
    return 'positive';
  }
  if (
    [
      'REJECTED',
      'FAILED',
      'CANCELLED',
      'BLOCKED',
      'DUPLICATE',
      'SUSPENDED',
      'REVOKED',
      'ERROR',
      'EXPIRED',
    ].includes(value)
  ) {
    return 'negative';
  }
  if (
    [
      'PENDING_APPROVAL',
      'ACCOUNTING_VERIFICATION',
      'TREASURY_APPROVAL',
      'RETURNED',
      'REVIEW_REQUIRED',
      'SUGGESTED',
      'IN_PROGRESS',
      'PENDING',
      'PARTIAL',
      'P1',
      'P2',
    ].includes(value)
  ) {
    return 'attention';
  }
  if (
    [
      'PAYMENT_PROCESSING',
      'PROCESSING',
      'EXPORTED',
      'BATCHED',
      'PARTIALLY_RECONCILED',
      'EXTRACTING',
      'QUEUED',
      'RUNNING',
    ].includes(value)
  ) {
    return 'progress';
  }
  // SKIPPED falls through to neutral on purpose: an email we deliberately left
  // alone is an expected outcome, not a problem to colour like one.
  return 'neutral';
}

/**
 * Right-aligned monetary cell with tabular figures.
 *
 * In the amount's own currency (the selected company's unless given), or as
 * `≈ ₹` when the common "View in" dropdown asks for rupees. The tooltip always
 * shows the other view.
 */
export function Money({
  minor,
  compact = false,
  className = '',
  currency,
}: {
  minor: number | null | undefined;
  compact?: boolean;
  className?: string;
  currency?: string;
}) {
  const money = useMoney();
  if (minor === null || minor === undefined) return <span className="muted">—</span>;
  const options = { currency };
  return (
    <span
      className={`tabular ${className}`}
      title={money.alternate(minor, options) ?? money.native(minor, options)}
    >
      {compact ? money.compact(minor, options) : money.format(minor, options)}
    </span>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  inlineActions = false,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /**
   * Keep the actions beside the title instead of letting them drop below it,
   * for compact (icon) actions on a narrow screen.
   */
  inlineActions?: boolean;
}) {
  return (
    <div
      className={`mb-6 flex items-start justify-between gap-4 ${inlineActions ? '' : 'flex-wrap'}`}
    >
      <div className={inlineActions ? 'min-w-0' : undefined}>
        <h1 className="text-2xl font-semibold tracking-tight text-ink-900">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-ink-500">{subtitle}</p> : null}
      </div>
      {actions ? (
        <div className={`flex items-center gap-2 ${inlineActions ? 'shrink-0' : 'flex-wrap'}`}>
          {actions}
        </div>
      ) : null}
    </div>
  );
}

export function Card({
  children,
  className = '',
  variant = 'default',
}: {
  children: ReactNode;
  className?: string;
  /** `hero` is the dark teal panel; `dark` the Black Steel total card. */
  variant?: 'default' | 'hero' | 'dark';
}) {
  const base = { default: 'card', hero: 'card-hero', dark: 'card-dark' }[variant];
  return <div className={`${base} ${className}`}>{children}</div>;
}

export function StatCard({
  label,
  value,
  sub,
  tone = 'default',
  icon: IconGlyph,
  delta,
  variant = 'default',
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'default' | 'warning' | 'danger' | 'success';
  icon?: LucideIcon;
  /**
   * A real period-over-period movement.
   *
   * Deliberately optional and deliberately rare: inventing a percentage the
   * API does not report would put a number a CFO might act on in front of
   * them. Only pass this where the server actually supplies a comparison.
   */
  delta?: { percent: number; label?: string };
  variant?: 'default' | 'hero';
}) {
  const hero = variant === 'hero';
  const accent = hero
    ? 'text-white'
    : {
        default: 'text-ink-900',
        warning: 'text-amber-700',
        danger: 'text-red-700',
        success: 'text-emerald-700',
      }[tone];

  const chip = hero
    ? 'chip-on-dark'
    : {
        default: 'chip-brand',
        warning: 'chip-amber',
        danger: 'chip-danger',
        success: 'chip-brand',
      }[tone];

  const rising = (delta?.percent ?? 0) >= 0;
  const DeltaGlyph = rising ? TrendingUp : TrendingDown;

  return (
    <Card variant={hero ? 'hero' : 'default'} className="p-5">
      {IconGlyph || delta ? (
        <div className="mb-3 flex items-start justify-between gap-3">
          {IconGlyph ? (
            <span className={chip}>
              <IconGlyph className="h-5 w-5" aria-hidden="true" />
            </span>
          ) : (
            <span />
          )}
          {delta ? (
            <span className={hero ? 'delta-on-dark' : rising ? 'delta-up' : 'delta-down'}>
              <DeltaGlyph className="h-3 w-3" aria-hidden="true" />
              {rising ? '+' : '−'}
              {Math.abs(delta.percent)}%
            </span>
          ) : null}
        </div>
      ) : null}

      <p className={hero ? 'stat-label text-brand-100' : 'stat-label'}>{label}</p>
      <p className={`stat-value ${accent}`}>{value}</p>
      {sub ? (
        <p className={`mt-1 text-xs ${hero ? 'text-brand-100' : 'text-ink-500'}`}>{sub}</p>
      ) : null}
      {delta?.label ? (
        <p className={`mt-1 text-xs ${hero ? 'text-brand-100' : 'text-ink-400'}`}>{delta.label}</p>
      ) : null}
    </Card>
  );
}

export function EmptyState({
  title,
  hint,
  illustration = 'no-documents',
  action,
}: {
  title: string;
  hint?: string;
  illustration?: IllustrationName;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-4 py-12 text-center">
      <Illustration name={illustration} className="h-40 sm:h-48" />
      <p className="mt-4 text-sm font-semibold text-ink-700">{title}</p>
      {hint ? <p className="mt-1 text-sm text-ink-500">{hint}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div
      role="status"
      className="flex items-center justify-center gap-3 py-12 text-sm text-ink-500"
    >
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-brand-100 border-t-brand-700" />
      {label ?? 'Loading…'}
    </div>
  );
}

/**
 * Error display. A 403 is shown as a permission message rather than a failure,
 * because it is an expected outcome of the role model, not a bug.
 */
/**
 * A failure, in plain words.
 *
 * A validation failure lists what to fix, field by field — except the fields
 * a form already marks under the input itself (`inlineFields`); when every
 * problem is shown there, this renders nothing. Anything else (no connection,
 * no permission, a conflict) is one sentence.
 */
export function ErrorState({
  error,
  compact = false,
  labels,
  inlineFields,
}: {
  error: unknown;
  compact?: boolean;
  /** Names for fields, by key, where the key alone reads badly. */
  labels?: Record<string, string>;
  /** Fields whose problems the form already shows under the input. */
  inlineFields?: readonly string[];
}) {
  const forbidden = (error as { status?: number } | null)?.status === 403;
  const allProblems = problemsOf(error);
  const problems = allProblems.filter((problem) => !inlineFields?.includes(fieldOf(problem)));

  if (allProblems.length) {
    if (!problems.length) return null;
    return (
      <div className="notice-danger" role="alert">
        <p className="font-medium">Some details need fixing:</p>
        <ul className="mt-1 list-disc space-y-0.5 pl-5">
          {problems.map((problem) => (
            <li key={`${problem.path}:${problem.message}`}>
              {labels?.[fieldOf(problem)] ?? fieldLabel(problem.path)}: {problem.message}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const message = plainErrorMessage(error);

  // Inside a dialog or a table cell an illustration would dominate the message.
  if (compact) {
    return <div className={forbidden ? 'notice-warning' : 'notice-danger'}>{message}</div>;
  }

  return (
    <div className={forbidden ? 'notice-warning' : 'notice-danger'}>
      <div className="flex flex-col items-center gap-3 py-4 text-center sm:flex-row sm:text-left">
        <Illustration name={forbidden ? 'no-access' : 'broken'} className="h-28 shrink-0 sm:h-32" />
        <p>{message}</p>
      </div>
    </div>
  );
}

export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-ink-200">{children}</table>
    </div>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange(page: number): void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;

  return (
    <div className="flex items-center justify-between gap-3 border-t border-ink-100 px-4 py-3.5 text-sm text-ink-600 sm:px-5">
      <span>
        {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of{' '}
        {total.toLocaleString('en-IN')}
      </span>
      <div className="flex gap-2">
        {/* On a phone the arrows alone carry the buttons; the name stays for screen readers. */}
        <button
          className="btn-secondary"
          aria-label="Previous"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Previous</span>
        </button>
        <button
          className="btn-secondary"
          aria-label="Next"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ key: T; label: string; count?: number }>;
  active: T;
  onChange(key: T): void;
}) {
  return (
    <div role="tablist" className="flex flex-wrap gap-1 border-b border-ink-100 px-2 pt-2">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          role="tab"
          aria-selected={active === tab.key}
          onClick={() => onChange(tab.key)}
          className={`rounded-t-lg px-4 py-2 text-sm font-medium transition-colors ${
            active === tab.key
              ? 'bg-brand-50 text-brand-800'
              : 'text-ink-500 hover:bg-ink-50 hover:text-ink-900'
          }`}
        >
          {tab.label}
          {tab.count !== undefined ? (
            <span className="ml-2 rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-600">
              {tab.count}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

/** A search box with a leading icon; the search runs on Enter. */
export function SearchInput({
  placeholder,
  defaultValue,
  onSearch,
  label = 'Search',
}: {
  placeholder?: string;
  defaultValue?: string;
  onSearch(value: string): void;
  label?: string;
}) {
  return (
    <div className="relative w-full">
      <Search
        className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400"
        aria-hidden="true"
      />
      <input
        type="search"
        className="input-search"
        aria-label={label}
        placeholder={placeholder}
        defaultValue={defaultValue}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onSearch((event.target as HTMLInputElement).value);
        }}
      />
    </div>
  );
}

/** Label/value pairs in two columns, for a record shown as a card. */
export function DetailList({
  items,
}: {
  items: Array<{ label: string; value: ReactNode; className?: string }>;
}) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
      {items.map((item) => (
        <div key={item.label} className="min-w-0">
          <dt className="text-xs text-ink-500">{item.label}</dt>
          <dd className={`truncate text-ink-900 ${item.className ?? ''}`}>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Modal({
  title,
  children,
  onClose,
  footer,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose(): void;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // With one dialog opened from another, Escape closes only the top one.
      const open = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
      if (open[open.length - 1] === dialog.current) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`card relative z-10 flex w-full flex-col ${wide ? 'max-w-4xl' : 'max-w-lg'} max-h-[90vh]`}
      >
        {/* Only the body scrolls: the title and the buttons stay on screen. */}
        <div className="panel-head shrink-0">
          <h2 className="text-lg font-semibold text-ink-900">{title}</h2>
          <button
            className="text-ink-400 transition-colors hover:text-ink-900"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-ink-100 px-5 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Destructive action guarded by a typed reason.
 *
 * Cancelling an invoice, deleting a statement or voiding a payroll batch all
 * need the same shape: explain the consequence, capture a reason for the
 * audit trail, and refuse to submit without one.
 */
export function ConfirmWithReason({
  title,
  description,
  actionLabel,
  requireReason = true,
  onClose,
  onConfirm,
  pending,
  error,
}: {
  title: string;
  description: ReactNode;
  actionLabel: string;
  requireReason?: boolean;
  onClose(): void;
  onConfirm(reason: string): void;
  pending?: boolean;
  error?: unknown;
}) {
  const [reason, setReason] = useState('');
  const tooShort = requireReason && reason.trim().length < 3;

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-danger"
            disabled={tooShort || pending}
            onClick={() => onConfirm(reason.trim())}
          >
            {pending ? 'Working…' : actionLabel}
          </button>
        </>
      }
    >
      <div className="text-sm text-ink-600">{description}</div>
      <label className="label mt-4" htmlFor="confirm-reason">
        Reason{requireReason ? '' : ' (optional)'}
      </label>
      <textarea
        id="confirm-reason"
        className="input"
        rows={3}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Recorded in the audit trail"
      />
      {error ? (
        <div className="mt-3">
          <ErrorState error={error} compact />
        </div>
      ) : null}
    </Modal>
  );
}

/** Confidence chip used by the review and reconciliation screens. */
export function ConfidenceBadge({
  value,
  prefix,
  title,
}: {
  value: number;
  /** Says what the percentage is about, e.g. "Auto" on a field extraction filled in. */
  prefix?: string;
  title?: string;
}) {
  const percent = value <= 1 ? Math.round(value * 100) : Math.round(value);
  const tone: Tone = percent >= 90 ? 'positive' : percent >= 75 ? 'attention' : 'negative';
  return (
    <span data-tone={tone} className={`${BADGE[tone]} rounded-md px-1.5`} title={title}>
      {prefix ? `${prefix} · ` : ''}
      {percent}%
    </span>
  );
}

/**
 * Marks an invoice field a person changed from what extraction read
 * (`edited`), or filled in where extraction read nothing (`manual`).
 */
export function EditedBadge({ kind, title }: { kind: 'edited' | 'manual'; title?: string }) {
  return (
    <span className="badge badge-edited rounded-md px-1.5" title={title}>
      {kind === 'edited' ? 'Edited' : 'Manual'}
    </span>
  );
}

/** Bytes as a person reads them — one decimal, never more than it earns. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/** `.xlsx,.xls,.csv` and `image/png` alike, spelled the way the hint should read. */
function describeAccept(accept?: string): string | null {
  if (!accept) return null;
  const names = accept
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => (token.startsWith('.') ? token.slice(1) : (token.split('/').pop() ?? token)))
    .map((token) => (token === 'jpeg' ? 'JPG' : token.toUpperCase()));
  return names.length ? [...new Set(names)].join(' · ') : null;
}

/**
 * File picker as a drop zone.
 *
 * The native control renders a browser-drawn "Choose File" button whose label,
 * type and metrics no stylesheet can touch, and which says nothing about what
 * the screen will accept. The input is still the input — kept in the page,
 * focusable, and the thing the label points at — but it is visually replaced by
 * a target that states the formats, takes a drag, and reports what was chosen.
 */
export function FileField({
  id,
  file,
  onChange,
  accept,
  hint,
  disabled = false,
}: {
  id: string;
  file: File | null;
  onChange: (file: File | null) => void;
  accept?: string;
  /** Overrides the formats line — say the size or row limits here. */
  hint?: string;
  disabled?: boolean;
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const formats = hint ?? describeAccept(accept);

  // Re-picking the same file after a clear fires no change event unless the
  // input's value is reset, so every path back to empty goes through here.
  const clear = () => {
    if (inputRef.current) inputRef.current.value = '';
    onChange(null);
  };

  return (
    <div>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        disabled={disabled}
        className="peer sr-only"
        onChange={(event) => onChange(event.target.files?.[0] ?? null)}
      />

      {file ? (
        <div className="flex items-center gap-3 rounded-xl border border-ink-200 bg-white p-3">
          <span className="chip-brand h-9 w-9">
            <FileText className="h-4 w-4" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-ink-900">{file.name}</span>
            <span className="block text-xs text-ink-500">{formatBytes(file.size)}</span>
          </span>
          <label
            htmlFor={id}
            className="cursor-pointer rounded-full px-2.5 py-1 text-xs font-semibold text-brand-700
                       hover:bg-brand-50 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500"
          >
            Replace
          </label>
          <button
            type="button"
            className="rounded-full p-1.5 text-ink-400 hover:bg-ink-100 hover:text-ink-900"
            onClick={clear}
            aria-label={`Remove ${file.name}`}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <label
          htmlFor={id}
          className={`flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed
                      px-4 py-6 text-center transition-colors
                      peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500
                      peer-disabled:cursor-not-allowed peer-disabled:opacity-60 ${
                        dragging
                          ? 'border-brand-500 bg-brand-50'
                          : 'border-ink-300 bg-ink-50/60 hover:border-brand-400 hover:bg-brand-50/40'
                      }`}
          onDragOver={(event) => {
            event.preventDefault();
            if (!disabled) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            if (disabled) return;
            const dropped = event.dataTransfer.files?.[0];
            if (dropped) onChange(dropped);
          }}
        >
          <span className="chip-brand h-10 w-10">
            <UploadCloud className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="text-sm text-ink-700">
            <span className="font-semibold text-brand-700">Choose a file</span> or drag it here
          </span>
          {formats ? <span className="text-xs text-ink-500">{formats}</span> : null}
        </label>
      )}
    </div>
  );
}
